import { useState, useRef, useEffect } from "react";
import { MoreVertical } from "lucide-react";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { setAgentSlug, setSelectedAgent } from "@/store/agentsSlice";
import { setConversationId, renameConversation, deleteConversationThunk } from "@/store/conversationsSlice";
import { useNavigate } from "react-router-dom";
import "../sidebar.css"; 

export function SidebarChatList({ expanded }: { expanded: boolean }) {
  const conversations = useAppSelector((s) => s.conversations.conversations);
  const agents = useAppSelector((s) => s.agents.agents);
  const navigate = useNavigate();
  if (!expanded) return null;

  const getAgent = (agentId: string) => {
    const agent = agents.find((a: any) => a.agentId === agentId);
    return agent || { agentName: "Unknown Agent", agentId };
  };

  return (
    <div className="ml-4 mt-1 space-y-1">
      {conversations.slice(0, 5).map((conv: any) => (
        <ConversationRow key={conv.conversationId} conv={conv} getAgent={getAgent} />
      ))}

      {conversations.length === 0 && (
        <div className="px-3 py-2 text-xs text-muted-foreground">
          No conversations yet
        </div>
      )}
        <button
        className="w-full text-left px-3 py-2 rounded-md text-sm text-primary cursor-pointer hover:bg-accent/50 font-medium"
        onClick={() => navigate("/conversations")}
      >
        View all
      </button>
    </div>
  );
}

/* ------------------------------------------
   Conversation Row Component (with kebab)
------------------------------------------- */
function ConversationRow({ conv, getAgent }) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const agent = getAgent(conv?.agent?.agentId || 0);

  const [menuOpen, setMenuOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [title, setTitle] = useState(conv.title);

  const menuRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  /* Close menu when clicking outside */
  useEffect(() => {
    function handleClickOutside(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  /* Auto-focus input when editing */
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleRename = () => {
    setIsEditing(false);
    const newTitle = title.trim();
    if (newTitle && newTitle !== conv.title) {
      dispatch(renameConversation(conv.conversationId, newTitle));
    }
  };


  const handleDelete = () => {
    dispatch(deleteConversationThunk(conv.conversationId));
  };

  return (
    <div className="group flex items-center justify-between px-3 py-2 rounded-md hover:bg-accent cursor-pointer">
      {/* LEFT SIDE: Title or Input */}
      <div
        className="flex-1 min-w-0"
        onClick={() => {
          dispatch(setAgentSlug(conv?.agent?.agentId));
          dispatch(setSelectedAgent(agent));
          dispatch(setConversationId(conv.conversationId));
          navigate(`/agent/${conv?.agent?.agentId || 0}/${conv.conversationId}`);
        }}
      >
        {isEditing ? (
          <input
            ref={inputRef}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onBlur={handleRename}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleRename();
              if (e.key === "Escape") {
                setIsEditing(false);
                setTitle(conv.title);
              }
            }}
            className="w-[180px] bg-white border border-gray-300 rounded px-2 py-1 text-sm shadow-sm"
            style={{ minWidth: "200px" }}
          />

        ) : (
          <>
            <div className="truncate text-sm">{conv.title || "Untitled conversation"}</div>
            <div className="text-xs text-muted-foreground mt-0.5">
              {conv?.agent?.agentName || agent.agentName}
            </div>
          </>
        )}
      </div>

      {/* RIGHT SIDE: Vertical Kebab Menu */}
      <div className="relative" ref={menuRef}>
        <button
          onClick={(e) => {
            e.stopPropagation();
            setMenuOpen(!menuOpen);
          }}
          className="p-1 rounded opacity-0 group-hover:opacity-100 hover:bg-gray-200 transition"
        >
          <MoreVertical className="h-4 w-4 text-gray-600" />
        </button>

        {menuOpen && (
          <div className="absolute right-0 mt-2 w-32 bg-white border border-gray-200 rounded-md shadow-lg z-50 context-menu">
            <button
              onClick={() => {
                setMenuOpen(false);
                setIsEditing(true);
              }}
              className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100"
            >
              Rename
            </button>

            <button
              onClick={handleDelete}
              className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50"
            >
              Delete
            </button>
          </div>
        )}
      </div>

    </div>
  );
}
